<template>
  <div class="component-input-managers">
    <FormWizardStepHeader
      title="Invite your team"
      optional
      description="Invite your team to collaborate managing your profile"
    />

    <table class="w-100">
      <tbody>
        <StartupUsersTableItem
          v-for="user in users"
          :key="user.id"
          :user="user"
          @remove="remove"
          @resend="resend"
        />
      </tbody>
    </table>

    <div v-if="hasProfileKey">
      <a class="company-form-step-managers__add" @click="isAddModalOpen = true"
        >+ Add another user</a
      >
      <UsersAddModal
        v-model="isAddModalOpen"
        type="investor"
        :profile-key="$route.params.investor"
        @add="onUserAdded"
      />
    </div>

    <div v-else>
      <div class="component-input-team__list">
        <div v-if="form.managers.length" class="mb-60">
          <SlickList
            v-if="form.managers.length > 1"
            :key="`slicklist-${form.managers.length}`"
            v-model="form.managers"
          >
            <SlickItem
              v-for="(item, index) in form.managers"
              :key="`index-${index}${item.name.first}${item.name.last}`"
              :index="index"
              class="component-input-team__list__item"
            >
              <CompanyTeamListItem
                :item="item"
                @edit="onEdit"
                @remove="onRemoveTeamItem"
              />
            </SlickItem>
          </SlickList>

          <div
            v-if="form.managers.length === 1"
            key="single"
            class="component-input-team__list__item"
          >
            <CompanyTeamListItem
              :item="form.managers[0]"
              @edit="onEdit"
              @remove="onRemoveTeamItem"
            />
          </div>
        </div>
      </div>

      <InvestorAddManagersForm
        :use-user-as-placeholder="false"
        :editing="editing"
        @add="onTeamMemberAdded"
        @cancel="editing = null"
      />
    </div>
  </div>
</template>

<script>
import StartupUsersTableItem from '@/components/startup/users/StartupUsersTableItem'
import UsersAddModal from '@/modals/users/UsersAdd'
import FormWizardStepHeader from '@/components/FormWizardStepHeader'
import UserModel from '@/models/User'
import actionsMixin from '@/mixins/actions'
import { SlickItem, SlickList } from 'vue-slicksort'
import InvestorAddManagersForm from '@/components/create-investors/InvestorAddManagersForm'
import CompanyTeamListItem from '@/components/create-startups/CompanyTeamListItem'

export default {
  name: 'PageClientStartupsUsers',
  components: {
    StartupUsersTableItem,
    UsersAddModal,
    FormWizardStepHeader,
    CompanyTeamListItem,
    InvestorAddManagersForm,
    SlickItem,
    SlickList
  },
  layout: 'investor',
  mixins: [actionsMixin],
  props: {
    $v: {
      type: Object,
      required: true
    },
    value: {
      type: Object
    },
    formMode: {
      type: String
    }
  },
  data() {
    return {
      isAddModalOpen: false,
      users: [],
      editing: null,
      form: {
        ...this.value
      }
    }
  },
  computed: {
    hasProfileKey() {
      return this.profileKey ? true : false
    },
    profileKey() {
      const profileKey = this.$route.params.investor
      return profileKey ? profileKey : null
    }
  },
  watch: {
    value(newValue) {
      this.form = newValue
    },
    form: {
      deep: true,
      handler() {
        this.$emit('input', this.form)
      }
    }
  },
  async created() {
    await this.loadCompanyManagers()
  },
  methods: {
    onUserAdded(user) {
      this.users.push(new UserModel(user))
    },
    async loadCompanyManagers() {
      try {
        if (this.profileKey) {
          const { data } = await this.$axios.get(
            '/investors/' + this.$route.params.investor + '/managers'
          )
          this.users = data.users.map((user) => new UserModel(user))
        }
      } catch (err) {
        console.log(err)
      }
    },
    async remove(user) {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        'Deleting the user means that you withdraw their access to manage the profile.',
        {
          title: 'Delete user?',
          modalClass: 'modal-bb-squared',
          okTitle: 'Delete user',
          cancelTitle: 'Cancel',
          cancelVariant: 'outline',
          footerClass: 'd-flex'
        }
      )

      if (!confirmed) {
        return
      }

      await this.performAction(
        user,
        () => {
          return this.$axios.delete(
            `/investors/${this.$route.params.investor}/managers/${user.id}`
          )
        },
        () => {
          this.users.splice(this.users.indexOf(user), 1)
        }
      )
    },
    resend(user) {
      return this.performAction(
        user,
        () => {
          return this.$axios.post(
            `/investors/${this.$route.params.investor}/managers/${user.id}/resend`
          )
        },
        () => {}
      )
    },
    onEdit(item) {
      this.editing = item
    },
    onTeamMemberAdded(member) {
      if (this.editing) {
        Object.assign(this.editing, member)
        this.editing = null
      } else {
        this.form.managers.push(member)
      }
    },
    onRemoveTeamItem(member) {
      if (this.editing === member) {
        this.editing = null
      }

      const index = this.form.managers.indexOf(member)
      this.form.managers.splice(index, 1)
    }
  }
}
</script>

<style lang="scss" scoped>
.company-form-step-managers {
  &__add {
    display: inline-block;
    color: $blue-900 !important;
    margin-top: 2.5rem;
    cursor: pointer;
  }
}
</style>
